// Tema claro/oscuro/sistema. Default = oscuro. Aplica la clase .light en <html>
// (el default oscuro no necesita clase). Persiste en localStorage.
import { useEffect, useState } from "react";

export type ThemePref = "light" | "dark" | "system";
const KEY = "drnewenia.theme";

function systemIsLight(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: light)").matches;
}

export function applyTheme(pref: ThemePref): void {
  const isLight = pref === "light" || (pref === "system" && systemIsLight());
  document.documentElement.classList.toggle("light", isLight);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", isLight ? "#fafaf9" : "#0a0a0f");
}

export function useTheme(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(() => {
    try {
      const v = localStorage.getItem(KEY) as ThemePref | null;
      return v ?? "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    applyTheme(pref);
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const set = (p: ThemePref) => {
    try {
      localStorage.setItem(KEY, p);
    } catch {
      /* noop */
    }
    setPref(p);
  };

  return [pref, set];
}
